import React, { useState, useEffect, useRef } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';

import '../../styles/CreateEventForm.css';
import { ReactComponent as ArrowLeftIcon } from '../../react-icons/arrowhead_left.svg';
import { showMessage } from '../../showMessage';
import { ConfigSelect, type ConfigSelectOption } from '../shared/ConfigSelect';
import { GlassDatePicker } from '../shared/GlassDatePicker';

interface CreateEventFormProperties {
  onClose: () => void;
  onEventCreated: () => void;
}

interface Boat {
  id: number;
  name: string;
  boat_id: number;
}

interface BoatResponse {
  success: boolean;
  userBoats: Boat[];
  message?: string;
}

interface CreateEventResponse {
  success: boolean;
  message?: string;
}

const API_BASE_URL = String(process.env.REACT_APP_API_BASE_URL ?? '');

const readDefaultBoatIdFromConfig = (): number | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem('appConfig');
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { default_boat_id?: unknown };
    const rawDefaultId = parsed?.default_boat_id;
    if (typeof rawDefaultId === 'number') {
      return Number.isFinite(rawDefaultId) ? rawDefaultId : null;
    }
    if (typeof rawDefaultId === 'string') {
      const trimmed = rawDefaultId.trim();
      if (trimmed.length === 0) {
        return null;
      }
      const numericValue = Number(trimmed);
      return Number.isNaN(numericValue) ? null : numericValue;
    }
    return null;
  } catch {
    return null;
  }
};

export const CreateEventForm: React.FC<CreateEventFormProperties> = ({
  onClose,
  onEventCreated,
}) => {
  const { t } = useTranslation('common');
  const backLabel = t('modals.back');
  const [eventName, setEventName] = useState<string>('');
  const [eventDate, setEventDate] = useState<string>(() => {
    const today = new Date();
    const MINUTES_TO_MILLISECONDS = 60 * 1000;
    const local = new Date(today.getTime() - today.getTimezoneOffset() * MINUTES_TO_MILLISECONDS);
    return local.toISOString().split('T')[0];
  });
  const [boatId, setBoatId] = useState<number | null>(null);
  const [boats, setBoats] = useState<Boat[]>([]);
  const [error, setError] = useState<string | null>(null);
  const defaultBoatIdRef = useRef<number | null>(readDefaultBoatIdFromConfig());

  useEffect(() => {
    const fetchBoats = async (): Promise<void> => {
      const tokenRaw = localStorage.getItem('token');
      if (!tokenRaw) {
        setError(t('modals.missingAuthToken'));
        return;
      }
      const token: string = tokenRaw;

      try {
        const response = await axios.post<BoatResponse>(
          `${API_BASE_URL}/api/user/getUserBoats`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.data.success && Array.isArray(response.data.userBoats)) {
          const fetchedBoats = response.data.userBoats;
          setBoats(fetchedBoats);
          const desiredBoatId = defaultBoatIdRef.current;
          if (desiredBoatId !== null) {
            const matchingBoat = fetchedBoats.find(
              (boat) => boat.id === desiredBoatId || boat.boat_id === desiredBoatId,
            );
            if (matchingBoat) {
              setBoatId(matchingBoat.id);
              defaultBoatIdRef.current = null;
            }
          }
        } else {
          setError(response.data.message ?? t('modals.failedFetchBoats'));
        }
      } catch {
        setError(t('modals.networkFetchBoats'));
      }
    };

    void fetchBoats();
  }, []);

  const handleSubmit = async (event_: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event_.preventDefault();
    setError(null);

    if (!eventName.trim() || !eventDate || !boatId) {
      setError(t('modals.pleaseCompleteFields'));
      return;
    }

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(eventDate)) {
      setError(t('modals.dateFormat'));
      return;
    }

    const tokenRaw = localStorage.getItem('token');
    if (!tokenRaw) {
      setError(t('modals.missingAuthToken'));
      return;
    }
    const token: string = tokenRaw;

    try {
      const createEventUrl = `${API_BASE_URL}/api/user/createEvent`;
      const response = await axios.post<CreateEventResponse>(
        createEventUrl,
        {
          event_name: eventName,
          event_date: eventDate,
          user_boat_id: boatId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        onEventCreated();
        onClose();
        showMessage('success', t('notifications.success'), t('modals.eventCreated'));
      } else {
        setError(response.data.message ?? t('modals.failedCreateEvent'));
      }
    } catch {
      setError(t('modals.networkCreateEvent'));
    }
  };

  return (
    <div className="create-event-modal" role="dialog" aria-modal="true">
      <button type="button" className="create-event-back" onClick={onClose} aria-label={backLabel}>
        <ArrowLeftIcon aria-hidden="true" />
        <span className="create-event-back-text">{backLabel}</span>
      </button>
      <header className="create-event-header">
        <h2>{t('modals.createEventTitle')}</h2>
      </header>
      <form
        className="CreateEventForm-form"
        onSubmit={handleSubmit}
        aria-describedby={error ? 'create-event-error' : undefined}
      >
        <div className="CreateEventForm-field">
          <label htmlFor="eventName">{t('modals.eventName')}</label>
          <input
            type="text"
            id="eventName"
            value={eventName}
            onChange={(event_: React.ChangeEvent<HTMLInputElement>) =>
              setEventName(event_.target.value)
            }
            autoComplete="off"
            required
          />
        </div>
        <div className="CreateEventForm-field CreateEventForm-field--date">
          <GlassDatePicker
            label={t('modals.eventDate')}
            placeholder={t('modals.placeholderDate')}
            value={eventDate}
            onChange={(value) => setEventDate(value)}
            className="create-event-date-picker"
            ariaLabel={t('modals.eventDate')}
          />
        </div>
        <div className="CreateEventForm-field">
          <label htmlFor="boat">{t('modals.boat')}</label>
          <ConfigSelect
            id="boat"
            options={boats.map<ConfigSelectOption>((boat) => ({
              value: String(boat.id),
              label: boat.name,
            }))}
            placeholder={t('modals.selectBoat')}
            value={boatId === null ? '' : String(boatId)}
            onChange={(value) => {
              if (!value) {
                setBoatId(null);
                return;
              }
              const parsedValue = Number.parseInt(value, 10);
              setBoatId(Number.isNaN(parsedValue) ? null : parsedValue);
            }}
          />
        </div>
        {error && (
          <p id="create-event-error" className="error">
            {error}
          </p>
        )}
        <div className="buttons-group">
          <button type="submit" className="primary">
            {t('modals.createEvent')}
          </button>
        </div>
      </form>
    </div>
  );
};
